import { computePosition, autoUpdate, offset, flip } from '@floating-ui/dom';

export default () => ({
  open: false,
  cleanup: null,

  init() {
    this.setFloatingElement();
    this.generateItemsIds();

    document.addEventListener('click', (event) => this.clickOutside(event));
  },

  destroy() {
    if (this.cleanup) {
      this.cleanup();
    }

    document.removeEventListener('click', (event) => this.clickOutside(event));
  },

  selectItem(event) {
    event.preventDefault();

    const items = this.$el.querySelectorAll('.item');

    items.forEach((item) => {
      if (item.dataset.value === event.target.dataset.value) {
        item.setAttribute('aria-selected', true);
      } else {
        item.removeAttribute('aria-selected');
      }
    });

    const input = this.$refs.input;
    const valueDisplay = this.$refs.value;

    const oldValue = input.value;
    const newValue = event.target.dataset.value;

    input.value = newValue;
    valueDisplay.innerText = event.target.innerText;

    if (oldValue !== newValue) {
      const changeEvent = new InputEvent('change', {
        bubbles: true,
        cancelable: true,
      });

      input.dispatchEvent(changeEvent);
    }

    this.closeContent();
  },

  onClick() {
    this.toggleContent();

    if (this.open) {
      this.setFocusAndCurrent();
    } else {
      this.resetCurrent();
    }
  },

  handleKeyDown(event) {
    event.preventDefault();

    const items = this.$el.querySelectorAll('[role="option"]');

    const currentIndex = Array.from(items).findIndex(
      (item) => item.getAttribute('aria-current') === 'true',
    );

    if (currentIndex + 1 < items.length) {
      items[currentIndex].removeAttribute('aria-current');
      this.setAriaCurrentAndActiveDescendant(currentIndex + 1);
    }
  },

  handleKeyUp(event) {
    event.preventDefault();

    const items = this.$el.querySelectorAll('[role="option"]');

    const currentIndex = Array.from(items).findIndex(
      (item) => item.getAttribute('aria-current') === 'true',
    );

    if (currentIndex > 0) {
      items[currentIndex].removeAttribute('aria-current');
      this.setAriaCurrentAndActiveDescendant(currentIndex - 1);
    }
  },

  handleEsc(event) {
    event.preventDefault();
    this.closeContent();
  },

  setFocusAndCurrent() {
    const trigger = this.$refs.trigger;
    const items = this.$el.querySelectorAll('[role="option"]');

    const selectedItem = Array.from(items).find(
      (item) => item.getAttribute('aria-selected') === 'true',
    );

    if (selectedItem) {
      selectedItem.focus({ preventScroll: true });
      selectedItem.setAttribute('aria-current', 'true');

      trigger.setAttribute('aria-activedescendant', selectedItem.getAttribute('id'));
    } else if (items.length > 0) {
      items[0].focus({ preventScroll: true });
      items[0].setAttribute('aria-current', 'true');

      trigger.setAttribute('aria-activedescendant', items[0].getAttribute('id'));
    }
  },

  resetCurrent() {
    const items = this.$el.querySelectorAll('[role="option"]');

    items.forEach((item) => item.removeAttribute('aria-current'));
  },

  clickOutside(event) {
    if (!this.open) return;
    if (this.$el.contains(event.target)) return;

    event.preventDefault();
    this.toggleContent();
  },

  toggleContent() {
    this.open = !this.open;
    const content = this.$refs.content;
    content.classList.toggle('hidden');

    const trigger = this.$refs.trigger;
    trigger.setAttribute('aria-expanded', this.open);
  },

  setFloatingElement() {
    const trigger = this.$refs.trigger;
    const content = this.$refs.content;

    this.cleanup = autoUpdate(trigger, content, () => {
      computePosition(trigger, content, {
        middleware: [offset(4), flip()],
      }).then(({ x, y }) => {
        Object.assign(content.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      });
    });
  },

  generateItemsIds() {
    const content = this.$refs.content;
    const trigger = this.$refs.trigger;
    const items = this.$el.querySelectorAll('[role="option"]');

    const contentId = content.getAttribute('id');
    trigger.setAttribute('aria-controls', contentId);

    items.forEach((item, index) => {
      item.id = `${contentId}-${index}`;
    });
  },

  setAriaCurrentAndActiveDescendant(currentIndex) {
    const items = this.$el.querySelectorAll('[role="option"]');
    const trigger = this.$refs.trigger;

    const currentItem = items[currentIndex];
    currentItem.focus({ preventScroll: true });
    currentItem.setAttribute('aria-current', 'true');
    trigger.setAttribute('aria-activedescendant', currentItem.getAttribute('id'));
  },

  closeContent() {
    this.toggleContent();
    this.resetCurrent();

    const trigger = this.$refs.trigger;
    trigger.setAttribute('aria-activedescendant', true);
    trigger.focus({ preventScroll: true });
  },
});
